import { RefObject, useMemo } from "react";
import { Toast } from "primereact/toast";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { showMsg } from "../../utils/Constants.tsx";
import {
  Catalogos,
  FiltrosReporte,
  clean,
  formatIsoDate,
  quickRanges,
  toIsoDate,
} from "./reportesUtils.ts";
import "../../screens/finanzas/dashboard.css";
import "./reportes.css";

interface ReportesHeaderProps {
  eyebrow: string;
  title: string;
  icon: string;
  filtros: FiltrosReporte;
  catalogos: Catalogos | null;
  toast: RefObject<Toast>;
  // Show the "tipo documento" filter (solicitudes only: ids 1-4)
  tiposDocumento?: { label: string; value: number }[];
  // Wording of the extra scope of users without cost center 29 (cost
  // centers outside theirs, reached through solicitudes they created)
  textoPropias?: { opcion: string; chip: string };
  loading: boolean;
  exporting: boolean;
  onReload: () => void;
  onExport: () => void;
}

/**
 * Hero header + filter panel shared by the solicitudes / órdenes dashboards.
 * Same look and behavior as the finanzas dashboard (screens/finanzas).
 */
export default function ReportesHeader({
  eyebrow,
  title,
  icon,
  filtros,
  catalogos,
  toast,
  tiposDocumento,
  textoPropias = { opcion: "solo tus solicitudes", chip: "tus solicitudes" },
  loading,
  exporting,
  onReload,
  onExport,
}: ReportesHeaderProps) {
  const {
    form,
    setForm,
    applied,
    query,
    isDirty,
    patchBoth,
    alcance,
    centrosPropios,
    incluyePropias,
  } = filtros;

  // Cost centers the user may pick: all with cost center 29; otherwise the
  // ones in their centros_costo_vista plus (solicitudes dashboard) those
  // where they created solicitudes
  const centrosVisibles = useMemo(() => {
    const todos = catalogos?.centrosCosto ?? [];
    if (alcance.total) return todos;
    const permitidos = new Set([...alcance.centros, ...centrosPropios]);
    return todos.filter((c) => permitidos.has(c.id));
  }, [catalogos, alcance, centrosPropios]);

  // Áreas: all with cost center 29, otherwise those of the user's centers
  const areaOptions = useMemo(() => {
    const areasConCentros = new Set(centrosVisibles.map((c) => c.idArea));
    return (catalogos?.areas ?? [])
      .filter((a) => alcance.total || areasConCentros.has(a.id))
      .map((a) => ({
        label: clean(a.nombre),
        value: a.id,
      }));
  }, [catalogos, alcance, centrosVisibles]);

  // Selecting an área narrows the cost centers offered
  const centroCostoOptions = useMemo(
    () =>
      centrosVisibles
        .filter((c) => !form.idArea || c.idArea === form.idArea)
        .map((c) => ({
          label: clean(c.nombre),
          value: c.id,
          // Outside their cost centers: only their own solicitudes show
          area: centrosPropios.includes(c.id)
            ? `${clean(c.area)} · ${textoPropias.opcion}`
            : clean(c.area),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, "es")),
    [centrosVisibles, form.idArea, centrosPropios, textoPropias.opcion],
  );

  const onAreaChange = (idArea: number | null) => {
    const allowed = new Set(
      centrosVisibles
        .filter((c) => !idArea || c.idArea === idArea)
        .map((c) => c.id),
    );
    setForm((f) => ({
      ...f,
      idArea,
      centrosCosto: f.centrosCosto.filter((id) => allowed.has(id)),
    }));
  };

  const apply = () => {
    if (
      form.fechaDesde &&
      form.fechaHasta &&
      form.fechaDesde > form.fechaHasta
    ) {
      showMsg(
        toast,
        "warn",
        "Filtros",
        "La fecha desde no puede ser mayor que la fecha hasta",
      );
      return;
    }
    filtros.apply();
  };

  const rangos = quickRanges();
  const rangoActivo = rangos.find(
    (q) =>
      toIsoDate(q.desde) === query.fechaDesde &&
      toIsoDate(q.hasta) === query.fechaHasta,
  )?.label;

  const ccLabel = (id: number) =>
    clean(catalogos?.centrosCosto.find((c) => c.id === id)?.nombre) ||
    `CC ${id}`;

  return (
    <>
      {/* ------------------------- HEADER ------------------------- */}
      <header className="fd-hero">
        <div className="fd-hero-main">
          <div className="fd-hero-icon" aria-hidden="true">
            <i className={`pi ${icon}`} />
          </div>
          <div className="fd-hero-text">
            <span className="fd-eyebrow">{eyebrow}</span>
            <h4 className="fd-title">{title}</h4>
            <div className="fd-meta">
              <span className="fd-meta-item">
                <i className="pi pi-calendar" />
                {formatIsoDate(query.fechaDesde)}
                <i className="pi pi-arrow-right fd-meta-arrow" />
                {formatIsoDate(query.fechaHasta)}
              </span>
              <span className="fd-meta-item">
                <i className="pi pi-clock" />
                Por fecha de creación
              </span>
              {!alcance.total ? (
                <span
                  className="fd-meta-item"
                  title="Los datos se limitan a los centros de costo de tu perfil"
                >
                  <i className="pi pi-lock" />
                  {alcance.centros.length === 1
                    ? "Tu centro de costo"
                    : `Tus ${alcance.centros.length} centros de costo`}
                  {incluyePropias ? ` + ${textoPropias.chip}` : ""}
                </span>
              ) : null}
              {filtros.activeCount > 0 ? (
                <span className="fd-meta-item fd-meta-accent">
                  <i className="pi pi-filter-fill" />
                  {filtros.activeCount}{" "}
                  {filtros.activeCount === 1
                    ? "filtro activo"
                    : "filtros activos"}
                </span>
              ) : null}
            </div>
          </div>
        </div>
        <div className="fd-hero-actions">
          <Button
            type="button"
            icon="pi pi-refresh"
            label="Actualizar"
            className="fd-btn fd-btn-ghost"
            loading={loading}
            onClick={onReload}
          />
          <Button
            type="button"
            icon="pi pi-file-excel"
            label="Exportar Excel"
            className="fd-btn fd-btn-excel"
            loading={exporting}
            disabled={loading}
            onClick={onExport}
          />
        </div>
      </header>

      {/* ------------------------- FILTROS ------------------------- */}
      <section className="fd-filters" aria-label="Filtros del reporte">
        <div className="fd-filters-head">
          <span className="fd-filters-title">
            <i className="pi pi-sliders-h" />
            Filtros
          </span>
          {isDirty ? (
            <span className="fd-dirty">
              <span className="fd-dirty-dot" />
              Cambios sin aplicar
            </span>
          ) : null}
        </div>

        <div className="fd-filter-grid">
          <div className="fd-field">
            <label htmlFor="rp-desde">Desde</label>
            <Calendar
              inputId="rp-desde"
              value={form.fechaDesde}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  fechaDesde: (e.value as Date) ?? null,
                }))
              }
              dateFormat="dd/mm/yy"
              placeholder="dd/mm/aaaa"
              showIcon
              showButtonBar
              maxDate={form.fechaHasta ?? undefined}
            />
          </div>
          <div className="fd-field">
            <label htmlFor="rp-hasta">Hasta</label>
            <Calendar
              inputId="rp-hasta"
              value={form.fechaHasta}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  fechaHasta: (e.value as Date) ?? null,
                }))
              }
              dateFormat="dd/mm/yy"
              placeholder="dd/mm/aaaa"
              showIcon
              showButtonBar
              minDate={form.fechaDesde ?? undefined}
            />
          </div>
          <div className="fd-field">
            <label htmlFor="rp-area">Área</label>
            <Dropdown
              inputId="rp-area"
              value={form.idArea}
              options={areaOptions}
              placeholder={alcance.total ? "Todas" : "Todas las tuyas"}
              showClear
              filter
              onChange={(e) => onAreaChange(e.value ?? null)}
            />
          </div>
          <div className="fd-field">
            <label htmlFor="rp-cc">Centros de costo</label>
            <MultiSelect
              inputId="rp-cc"
              value={form.centrosCosto}
              options={centroCostoOptions}
              placeholder={alcance.total ? "Todos" : "Todos los tuyos"}
              filter
              showClear
              maxSelectedLabels={1}
              selectedItemsLabel="{0} centros de costo"
              itemTemplate={(o: { label: string; area: string }) => (
                <div className="rp-cc-option">
                  <span>{o.label}</span>
                  <span className="fd-sub">{o.area}</span>
                </div>
              )}
              onChange={(e) =>
                setForm((f) => ({ ...f, centrosCosto: e.value ?? [] }))
              }
            />
          </div>
          <div className="fd-field">
            <label htmlFor="rp-nro-sol">N° solicitud</label>
            <InputNumber
              inputId="rp-nro-sol"
              value={form.idSolicitud}
              placeholder="Todas"
              useGrouping={false}
              min={1}
              onValueChange={(e) =>
                setForm((f) => ({ ...f, idSolicitud: e.value ?? null }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") apply();
              }}
            />
          </div>
          <div className="fd-field">
            <label htmlFor="rp-nro-oc">N° OC</label>
            <InputNumber
              inputId="rp-nro-oc"
              value={form.nroOrdenCompra}
              placeholder="Todas"
              useGrouping={false}
              min={1}
              onValueChange={(e) =>
                setForm((f) => ({ ...f, nroOrdenCompra: e.value ?? null }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") apply();
              }}
            />
          </div>
          {tiposDocumento ? (
            <div className="fd-field">
              <label htmlFor="rp-tipo">Tipo documento</label>
              <Dropdown
                inputId="rp-tipo"
                value={form.idTipoDocumento}
                options={tiposDocumento}
                placeholder="Todos"
                showClear
                onChange={(e) =>
                  setForm((f) => ({ ...f, idTipoDocumento: e.value ?? null }))
                }
              />
            </div>
          ) : null}
        </div>

        <div className="fd-filters-foot">
          <div className="fd-quick" role="group" aria-label="Rangos rápidos">
            <span className="fd-quick-label">Rangos rápidos</span>
            {rangos.map((q) => (
              <button
                key={q.label}
                type="button"
                className={`fd-pill${rangoActivo === q.label ? " is-active" : ""}`}
                aria-pressed={rangoActivo === q.label}
                onClick={() =>
                  patchBoth({ fechaDesde: q.desde, fechaHasta: q.hasta })
                }
              >
                {q.label}
              </button>
            ))}
          </div>
          <div className="fd-filter-actions">
            {applied.centrosCosto.length === 1 ? (
              <FilterChip
                icon="pi-sitemap"
                text={ccLabel(applied.centrosCosto[0])}
                title="Quitar filtro de centro de costo"
                onRemove={() => patchBoth({ centrosCosto: [] })}
              />
            ) : null}
            {applied.idUsuario ? (
              <FilterChip
                icon="pi-user"
                text={applied.usuarioNombre || `Usuario ${applied.idUsuario}`}
                title="Quitar filtro de usuario"
                onRemove={() =>
                  patchBoth({ idUsuario: null, usuarioNombre: "" })
                }
              />
            ) : null}
            {applied.idSolicitud ? (
              <FilterChip
                icon="pi-file-edit"
                text={`Solicitud N° ${applied.idSolicitud}`}
                title="Quitar filtro de solicitud"
                onRemove={() => patchBoth({ idSolicitud: null })}
              />
            ) : null}
            {applied.nroOrdenCompra ? (
              <FilterChip
                icon="pi-shopping-cart"
                text={`OC N° ${applied.nroOrdenCompra}`}
                title="Quitar filtro de orden de compra"
                onRemove={() => patchBoth({ nroOrdenCompra: null })}
              />
            ) : null}
            {applied.idProveedor ? (
              <FilterChip
                icon="pi-building"
                text={
                  applied.proveedorNombre || `Proveedor ${applied.idProveedor}`
                }
                title="Quitar filtro de proveedor"
                onRemove={() =>
                  patchBoth({ idProveedor: null, proveedorNombre: "" })
                }
              />
            ) : null}
            <Button
              type="button"
              label="Limpiar"
              icon="pi pi-filter-slash"
              className="fd-btn fd-btn-subtle"
              onClick={filtros.clear}
            />
            <Button
              type="button"
              label="Aplicar filtros"
              icon="pi pi-check"
              className={`fd-btn fd-btn-primary${isDirty ? " is-dirty" : ""}`}
              onClick={apply}
            />
          </div>
        </div>
      </section>
    </>
  );
}

function FilterChip({
  icon,
  text,
  title,
  onRemove,
}: {
  icon: string;
  text: string;
  title: string;
  onRemove: () => void;
}) {
  return (
    <span className="fd-chip">
      <i className={`pi ${icon}`} />
      <span className="fd-chip-text">{text}</span>
      <button
        type="button"
        className="fd-chip-close"
        title={title}
        aria-label={title}
        onClick={onRemove}
      >
        <i className="pi pi-times" />
      </button>
    </span>
  );
}
